import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { LeaderArcResource } from "@/lib/behavior/viewModels/coachSessionPrepVeera";

interface LeaderArcSectionProps {
  resource: LeaderArcResource;
  onOpenEvidenceReference?: (reference: {
    label: string;
    link: string | null;
    evidenceId?: string | null;
    sourceRevision?: string;
    source?: "coach_veera_brief";
  }) => void;
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-1.5">
      {items.map((item) => (
        <li key={item} className="text-sm leading-6 text-foreground">
          - {item}
        </li>
      ))}
    </ul>
  );
}

export function LeaderArcSection({
  resource,
  onOpenEvidenceReference,
}: LeaderArcSectionProps) {
  return (
    <Card className="border-border/60 bg-background/85">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Leader Arc</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 p-4 pt-0">
        <div className="space-y-1.5">
          <p className="text-sm leading-6 text-foreground">{resource.headline}</p>
          <p className="text-xs text-muted-foreground">
            <span className="font-medium capitalize">{resource.confidenceLabel}</span>
            {" "}
            ·
            {" "}
            {resource.confidenceNote}
          </p>
        </div>

        <div className="rounded-xl border border-border/60 bg-muted/15 p-3">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Unresolved Tension</p>
          <p className="mt-1 text-sm leading-6 text-foreground">{resource.unresolvedTension}</p>
        </div>

        <Accordion type="multiple" className="rounded-xl border border-border/60 px-3">
          <AccordionItem value="patterns" className="border-border/50">
            <AccordionTrigger className="py-3 text-left text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground hover:no-underline">
              Repeated Patterns
            </AccordionTrigger>
            <AccordionContent className="pb-3">
              <BulletList items={resource.repeatedPatterns} />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="changed" className="border-border/50">
            <AccordionTrigger className="py-3 text-left text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground hover:no-underline">
              What Changed
            </AccordionTrigger>
            <AccordionContent className="pb-3">
              <BulletList items={resource.whatChanged} />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="strengths" className="border-0">
            <AccordionTrigger className="py-3 text-left text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground hover:no-underline">
              Strengths
            </AccordionTrigger>
            <AccordionContent className="space-y-3 pb-3">
              <BulletList items={resource.strengths} />
              {resource.evidenceRefs.length > 0 ? (
                <div className="flex flex-wrap gap-2 pt-1">
                  {resource.evidenceRefs.map((entry) => (
                    <Button
                      key={entry.id}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onOpenEvidenceReference?.({
                        label: entry.label,
                        link: entry.route,
                        evidenceId: entry.id,
                        sourceRevision: entry.sourceRevision,
                        source: "coach_veera_brief",
                      })}
                    >
                      {entry.label}
                    </Button>
                  ))}
                </div>
              ) : null}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
}
